import React, { useCallback, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import interactionResponse from 'await-interaction-response';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { AccordionPanel } from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import getFilterTitle from '@/utilities/getFilterTitle';

import { commercialEntryPoint } from '@/analytics/srp/srpSearchEvents';

import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';

import CommercialSrpFilterEntryPoint from '@/components/CommercialSrpFilterEntryPoint/CommercialSrpFilterEntryPoint';

import FilterCheckboxes from './FilterCheckboxes';
import FilterPlaceholder from './FilterPlaceholder';

/**
 BodyStyleFilter renders a group of checkbox elements with image element and custom styling
**/

function BodyStyleFilter({
    onOptionChange = () => { },
    onOptionsClear = () => { },
    handleShowExpansion = () => { },
}) {
    const {
        commercial_entry_point: [enableCommercialEntryPoint],
        srp_body_style_filter_override: [enableCommercialBodyStyleFilterOverride, {
            label: commercialBodyStyleLabel,
        }],
    } = useFeatures([
        'commercial_entry_point',
        'srp_body_style_filter_override',
    ]);

    const bodyStyleOptions = useSelector(srpFiltersDuck.selectors.getBodyStyleOptions);
    const bodyStyleValues = useSelector(srpFiltersDuck.selectors.getBodyStyleValues);
    const filterValuesAnalytics = useSelector(srpFiltersDuck.selectors.getRequestValues);

    const filterOptions = useMemo(() => {

        const options = bodyStyleOptions?.options?.map((option) => {
            // Override van/minivan filter image and label in UI when in commercial experience.
            // Filtering logic isn't affected.
            if (enableCommercialBodyStyleFilterOverride && option.value === 'VANMV') {
                return {
                    ...option,
                    label: commercialBodyStyleLabel,
                };
            }

            return option;
        });
        return { ...bodyStyleOptions, options };
    }, [bodyStyleOptions, enableCommercialBodyStyleFilterOverride, commercialBodyStyleLabel]);

    const filterValues = useMemo(() => ({
        vehicleStyleCode: bodyStyleValues,
    }), [bodyStyleValues]);

    const filterName = 'vehicleStyleCode';

    const isExpanded = useSelector(useCallback((state) => srpFiltersDuck.selectors.isExpansionActive(state, filterName), [filterName]));

    const showFilter = useMemo(
        () => isExpanded && Object.keys(filterValues).length !== 0,
        [isExpanded, filterValues]
    );

    const title = useMemo(
        () => getFilterTitle('Body Style', bodyStyleValues),
        [bodyStyleValues]
    );

    const device = useDevice();
    const dispatch = useDispatch();

    const isXs = useMemo(() => device?.is?.xs, [device?.is?.xs]);

    const onCommercialEntryPointClick = useCallback(async (event) => {
        sendClick(commercialEntryPoint, event, {
            cmp: 'text-link::show-commercial-vehicles',
            eventSource: 'link',
            filterValues: filterValuesAnalytics,
        });

        if (isXs) {
            await interactionResponse();
            dispatch(srpFiltersDuck.creators.hideFilters());
        }
    }, [filterValuesAnalytics, dispatch, isXs]);

    const shouldExpand = useMemo(() => bodyStyleValues.length > 0, [bodyStyleValues.length]);

    // expand the panel if active options inside of it
    useEffect(() => {
        if (shouldExpand) {
            handleShowExpansion(filterName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldExpand, filterName]);

    const filterCheckboxesProps = useMemo(() => ({
        withAccordion: false,
        filter: filterOptions,
        values: filterValues,
        onOptionChange,
        onOptionsClear,
    }), [filterValues, filterOptions, onOptionChange, onOptionsClear]);

    const commercialEntryPointProps = useMemo(() => ({
        className: 'margin-top-3',
        onCommercialEntryPointClick,
        icon: '/cm-api/content/static/img/commercial/commercial-truck-Trans-small.png',
    }), [onCommercialEntryPointClick]);

    return (
        <AccordionPanel
            data-cmp="BodyStyleFilter"
            title={title}
            contentPadding={5}
            eventKey={filterName}
        >
            {!showFilter && <FilterPlaceholder uniqueKey={filterName} />}
            {showFilter && (
                <>
                    <FilterCheckboxes {...filterCheckboxesProps} />
                    {enableCommercialEntryPoint && (
                        <CommercialSrpFilterEntryPoint {...commercialEntryPointProps} />
                    )}
                </>
            )}
        </AccordionPanel>
    );
}

BodyStyleFilter.displayName = 'BodyStyleFilter';

export default React.memo(BodyStyleFilter);
